import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Dices, TrendingUp, FlaskConical, BarChart3, GraduationCap, Target } from "lucide-react";

const EducationCollege = () => {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Random Number Generator for College & Community College
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Professional-grade random number tools for statistics courses, research projects, lab experiments, and quantitative analysis. 
            Perfect for associate degree programs, technical colleges, and undergraduate coursework in STEM fields.
          </p>
        </header>

        {/* Key Benefits */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <BarChart3 className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Statistics & Data Science</h3>
              <p className="text-muted-foreground">
                Generate datasets for hypothesis testing, sampling distributions, confidence intervals, and regression analysis in intro stats courses.
              </p>
            </Card>
            <Card className="p-6">
              <FlaskConical className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-semibold mb-2">Lab Research Projects</h3>
              <p className="text-muted-foreground">
                Create randomized experimental designs, control/treatment group assignments, and sample selections for biology, chemistry, and psychology labs.
              </p>
            </Card>
            <Card className="p-6">
              <TrendingUp className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Business & Economics</h3>
              <p className="text-muted-foreground">
                Simulate market scenarios, generate random samples for surveys, and create Monte Carlo simulations for finance and accounting courses.
              </p>
            </Card>
          </div>
        </section>

        {/* Use Cases by Major */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">College Applications by Field</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Target className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">STEM Programs</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Statistics/Math:</strong> Generate random samples for Central Limit Theorem demonstrations, bootstrap sampling, and probability simulations</li>
                <li>• <strong>Computer Science:</strong> Test algorithms with random inputs, generate test data for databases, create random graphs for network analysis</li>
                <li>• <strong>Engineering:</strong> Monte Carlo simulations for reliability analysis, random noise generation for signal processing labs</li>
                <li>• <strong>Physics/Chemistry:</strong> Randomize experimental trials, simulate particle behavior, generate random measurement errors</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <GraduationCap className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Social Sciences & Business</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Psychology:</strong> Random assignment to experimental conditions, participant selection for surveys, stimulus randomization</li>
                <li>• <strong>Sociology:</strong> Random sampling for demographic studies, stratified random sampling for field research</li>
                <li>• <strong>Business/Marketing:</strong> A/B test group assignments, random customer selection for focus groups, price point testing</li>
                <li>• <strong>Economics:</strong> Generate random economic scenarios, simulate consumer choice models, Monte Carlo forecasting</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <FlaskConical className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Health Sciences & Nursing</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Nursing Programs:</strong> Random patient case studies, random selection for clinical rotations, randomized controlled trial simulations</li>
                <li>• <strong>Public Health:</strong> Epidemiological sampling, random cluster selection for community health studies</li>
                <li>• <strong>Allied Health:</strong> Random scheduling for lab practicals, generate random vital sign datasets for training</li>
                <li>• <strong>Pre-Med/Biology:</strong> Randomize genetic trait simulations, Mendelian genetics probability exercises</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <BarChart3 className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Research Methods & Projects</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Capstone Projects:</strong> Generate random datasets for analysis, randomize survey respondents, statistical power calculations</li>
                <li>• <strong>Thesis/Dissertation:</strong> Random sampling methodologies, experimental design randomization, Monte Carlo sensitivity analysis</li>
                <li>• <strong>Lab Reports:</strong> Create reproducible random data for methodology sections, random error simulation</li>
                <li>• <strong>Group Projects:</strong> Fair team assignments, random topic selection, randomized presentation orders</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Statistics Course Examples */}
        <section className="mb-16 bg-card/30 backdrop-blur-sm rounded-lg p-8 border border-border/50">
          <h2 className="text-3xl font-bold text-center mb-6">Statistics Course Applications</h2>
          <div className="max-w-4xl mx-auto space-y-6">
            <div>
              <h4 className="text-xl font-semibold mb-3">Intro Statistics (STAT 101)</h4>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• Generate random samples to demonstrate sampling distributions and Central Limit Theorem</li>
                <li>• Create datasets for calculating mean, median, standard deviation, and variance</li>
                <li>• Simulate coin flips and dice rolls to illustrate basic probability concepts</li>
                <li>• Random number selection for demonstrating Type I and Type II errors in hypothesis testing</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-3">Experimental Design</h4>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• Randomly assign subjects to control and treatment groups for lab experiments</li>
                <li>• Create randomized block designs for agricultural or medical studies</li>
                <li>• Generate random sequences for crossover designs and Latin square arrangements</li>
                <li>• Randomize order effects in repeated measures experiments</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-3">Applied Regression & Data Analysis</h4>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• Generate random error terms for regression simulations</li>
                <li>• Create synthetic datasets with known relationships for teaching regression diagnostics</li>
                <li>• Bootstrap resampling for confidence interval estimation</li>
                <li>• Monte Carlo simulations for prediction intervals and model validation</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tools Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-6">Professional Tools for College Students</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Target className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Random Number Generator</h3>
                <p className="text-muted-foreground">Generate uniform random samples in any range for statistical analysis and simulations</p>
              </Card>
            </Link>
            <Link to="/games">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Dices className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-semibold mb-2">Probability Tools</h3>
                <p className="text-muted-foreground">Dice rollers and coin flips for demonstrating probability distributions and expected values</p>
              </Card>
            </Link>
            <Link to="/lists">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <BarChart3 className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">List Randomizer</h3>
                <p className="text-muted-foreground">Shuffle and randomly select from custom lists for sampling and experimental design</p>
              </Card>
            </Link>
          </div>
        </section>

        {/* Academic Standards */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Curriculum Alignment</h2>
          <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-sm rounded-lg p-8 border border-border/50">
            <p className="text-muted-foreground mb-6">
              Our random number generator tools align with college-level learning outcomes and professional standards:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-lg mb-3">Statistics Education Standards</h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• ASA Guidelines for Assessment and Instruction in Statistics Education (GAISE)</li>
                  <li>• Understanding random sampling and randomization</li>
                  <li>• Simulation and computational methods</li>
                  <li>• Statistical inference and hypothesis testing</li>
                  <li>• Bootstrap and resampling techniques</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-3">Research & Lab Standards</h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• APA guidelines for experimental design</li>
                  <li>• NIH research methodology standards</li>
                  <li>• Randomized controlled trial protocols</li>
                  <li>• IRB-compliant randomization procedures</li>
                  <li>• Reproducible research practices</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Free Professional Tools for College Students</h2>
          <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            No login required. All tools are completely free for students, instructors, and researchers. Perfect for statistics, research methods, and quantitative coursework.
          </p>
          <Link to="/">
            <Button size="lg" className="text-lg px-8">
              Start Using Random Number Tools
            </Button>
          </Link>
        </section>
      </div>
    </Layout>
  );
};

export default EducationCollege;
